// The contract's names as the render's own keys.
//
// **The renderer draws from `src/api` facts and nothing else** (PLAN-CLIENT-SPLIT
// days 3–4), but its meshes, palettes and animation tables are still keyed by
// the numbers the rules used — a piece is `side << 3 | type`, a direction is
// 0..3 into `FACING_YAW`, a unit is a packed word the pile code sizes and
// colours by. This is the one place a name becomes such a key, so the views
// can stay as they are and the seam is a table rather than a rewrite.
//
// The numbers are `src/shared`'s — the vocabulary both trees read and neither
// owns — so nothing here imports the engine, and the render tree's lint fence
// is an error.

import type {
  BuildingName,
  Direction,
  ResourceName,
  PieceName,
  SquareFacts,
  StructureName,
  UnitFacts,
  VariantName
} from "src/api";
import {
  BISHOP,
  type Direction as Dir,
  EAST,
  KING,
  KNIGHT,
  makePiece,
  NORTH,
  PAWN,
  type Piece,
  type PieceType,
  QUEEN,
  ROOK,
  SOUTH,
  WEST
} from "src/shared/board";
import {
  ALTAR,
  FUEL,
  KEYSTONE,
  makeUnit,
  type MaterialLine,
  METAL,
  RELIC,
  SPECIAL,
  STONE,
  STORE,
  SIMPLE_WALL,
  FIRM_WALL,
  REINFORCED_WALL,
  ARMORY,
  COAL_GENERATOR,
  NUCLEAR_GENERATOR,
  BATTERY,
  CABLING_TOWER,
  TRACK,
  TRAIN_CAR,
  TRAIN_ENGINE,
  type StructureKind,
  TURRET,
  WELL,
  FUSION_CHAMBER,
  SHIELD_GENERATOR,
  ARTISAN_PILLAR,
  KINETIC_TURRET,
  CANNON_TURRET,
  PROPAGANDA_TOWER,
  HOUSE_OF_CARDS,
  ENDURANCE_MONUMENT,
  FIEND_FOUNTAIN,
  LEGION_TOWER,
  DRONE_WAREHOUSE,
  EMPOWER_TOWER
} from "src/shared/industry";
import {
  CONFESSOR,
  DAM,
  DREADNOUGHT,
  ALICORN,
  GRYPHON,
  JOAN_OF_ARC,
  JOAN_QUEEN,
  MARSHAL,
  MATRIARCH,
  MIRACLE_HEALER,
  PALADIN,
  PEGASUS,
  TURBO_BISHOP,
  TURBO_ROOK,
  UNICORN,
  VARIANT_PLAIN
} from "src/shared/variants";

export const DIR_INDEX: Record<Direction, Dir> = { N: NORTH, E: EAST, S: SOUTH, W: WEST };
export const DIR_NAME: Record<Dir, Direction> = ["N", "E", "S", "W"];

// No `monster`: retired and never emitted (`PieceName` keeps it for the human tree).
export const PIECE_TYPE = {
  pawn: PAWN,
  knight: KNIGHT,
  bishop: BISHOP,
  rook: ROOK,
  queen: QUEEN,
  king: KING
} as Record<PieceName, PieceType>;

export const STRUCTURE_KIND: Record<StructureName, StructureKind> = {
  store: STORE,
  turret: TURRET,
  altar: ALTAR,
  relic: RELIC,
  // Retired (§31 R1): no fact names it; the slot is the old wire number.
  banner: 4 as StructureKind,
  keystone: KEYSTONE,
  well: WELL,
  research: 7 as StructureKind
};

/** Every structure's true name to its kind (§31 S4–S6) — `building` on a fact or tile. */
export const BUILDING_KIND: Record<BuildingName, StructureKind> = {
  ...STRUCTURE_KIND,
  "simple-wall": SIMPLE_WALL,
  "firm-wall": FIRM_WALL,
  "reinforced-wall": REINFORCED_WALL,
  armory: ARMORY,
  "coal-generator": COAL_GENERATOR,
  "nuclear-generator": NUCLEAR_GENERATOR,
  battery: BATTERY,
  "cabling-tower": CABLING_TOWER,
  track: TRACK,
  "train-engine": TRAIN_ENGINE,
  "train-car": TRAIN_CAR,
  "fusion-chamber": FUSION_CHAMBER,
  "shield-generator": SHIELD_GENERATOR,
  "artisan-pillar": ARTISAN_PILLAR,
  // §31 S8; facts say `kind: "turret"` for all three, so each draws the gun.
  "kinetic-turret": KINETIC_TURRET,
  "cannon-turret": CANNON_TURRET,
  "propaganda-tower": PROPAGANDA_TOWER,
  "house-of-cards": HOUSE_OF_CARDS,
  "endurance-monument": ENDURANCE_MONUMENT,
  "fiend-fountain": FIEND_FOUNTAIN,
  "legion-tower": LEGION_TOWER,
  "drone-warehouse": DRONE_WAREHOUSE,
  "empower-tower": EMPOWER_TOWER
};

export const VARIANT_ID: Record<VariantName, number> = {
  plain: VARIANT_PLAIN,
  marshal: MARSHAL,
  dam: DAM,
  "turbo-bishop": TURBO_BISHOP,
  "turbo-rook": TURBO_ROOK,
  joan: JOAN_QUEEN,
  matriarch: MATRIARCH,
  confessor: CONFESSOR,
  healer: MIRACLE_HEALER,
  pegasus: PEGASUS,
  unicorn: UNICORN,
  gryphon: GRYPHON,
  paladin: PALADIN,
  dreadnought: DREADNOUGHT,
  "alicorn": ALICORN,
  "joan-of-arc": JOAN_OF_ARC
};

export const LINE_INDEX: Record<ResourceName, MaterialLine> = {
  metal: METAL,
  stone: STONE,
  special: SPECIAL,
  fuel: FUEL
};

/** The packed piece the views key their meshes by. */
export const packedPiece = (piece: Pick<NonNullable<SquareFacts["piece"]>, "type" | "side">): Piece =>
  makePiece(piece.side, PIECE_TYPE[piece.type]);

/** The packed unit word the pile code sizes and colours by. */
export const unitWord = (unit: UnitFacts): number => makeUnit(unit.grade, 0, LINE_INDEX[unit.line]);

/** A built part — never a drawn one, which is a claim and not a thing on the board. */
export const builtBelt = (facts: SquareFacts | undefined): NonNullable<SquareFacts["belt"]> | undefined =>
  facts?.belt && !facts.belt.blueprint ? facts.belt : undefined;
